import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogIn, LogOut, Package, Truck, CheckCircle, Clock,
  XCircle, ChevronLeft, ChevronRight, Eye, Send,
  BarChart3, DollarSign, ShoppingBag, Loader2, FileText, Percent, Smartphone, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { InvoicesTab, VatReportTab } from "./admin/InvoicesTab";
import { ManualPaymentsTab } from "./admin/ManualPaymentsTab";
import { PricingTab } from "./admin/PricingTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "In afwachting", color: "#F39C12", icon: Clock },
  payment_pending: { label: "Betaling wacht", color: "#E67E22", icon: Clock },
  paid: { label: "Betaald", color: "#27AE60", icon: DollarSign },
  processing: { label: "In verwerking", color: "#3498DB", icon: Package },
  shipped: { label: "Verzonden", color: "#8E44AD", icon: Truck },
  delivered: { label: "Afgeleverd", color: "#6AB04C", icon: CheckCircle },
  cancelled: { label: "Geannuleerd", color: "#E74C3C", icon: XCircle },
  expired: { label: "Verlopen", color: "#95A5A6", icon: Clock },
  failed: { label: "Mislukt", color: "#E74C3C", icon: XCircle },
};

function formatPrice(price) {
  if (!price) return "€0,00";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─── Login Screen ───
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Inloggen mislukt");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-[#FF3B30] text-2xl font-bold font-['Orbitron'] tracking-wider">TTW ENGINEERING</h1>
          <p className="text-[#555] text-sm mt-2">Admin Dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6 space-y-4" data-testid="admin-login-form">
          {error && <div className="bg-[#E74C3C]/10 border border-[#E74C3C]/30 rounded-lg px-4 py-2.5 text-[#E74C3C] text-sm" data-testid="admin-login-error">{error}</div>}
          <div>
            <label className="text-[#888] text-xs block mb-1.5">Gebruikersnaam</label>
            <Input data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} required className="bg-[#0A0A0A] border-[#1E1E1E] text-white focus:border-[#FF3B30]" placeholder="admin" />
          </div>
          <div>
            <label className="text-[#888] text-xs block mb-1.5">Wachtwoord</label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0A0A0A] border-[#1E1E1E] text-white focus:border-[#FF3B30]" placeholder="••••••••" />
          </div>
          <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#FF3B30] hover:bg-[#E0342A] text-white py-5 font-bold rounded-lg">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <span className="flex items-center justify-center gap-2"><LogIn size={18} /> Inloggen</span>}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ─── Status Badge ───
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#888", icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }} data-testid={`status-badge-${status}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

// ─── Order Detail Modal ───
function OrderDetail({ order, token, onClose, onUpdate }) {
  const [newStatus, setNewStatus] = useState(order.status);
  const [trackingCode, setTrackingCode] = useState(order.tracking_code || "");
  const [carrier, setCarrier] = useState(order.carrier || "PostNL");
  const [saving, setSaving] = useState(false);

  const handleStatusUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status bijgewerkt naar: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      onUpdate();
    } catch {
      toast.error("Status bijwerken mislukt");
    }
    setSaving(false);
  };

  const handleShip = async () => {
    if (!trackingCode.trim()) { toast.error("Voer een Track & Trace code in"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/orders/${order.id}/ship`, { tracking_code: trackingCode, carrier }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bestelling verzonden! Klant is op de hoogte gebracht.");
      onUpdate();
    } catch {
      toast.error("Verzenden mislukt");
    }
    setSaving(false);
  };

  const showoffItems = (order.items || []).filter((i) => !i.slug?.startsWith("ozparts-"));
  const ozpartsItems = (order.items || []).filter((i) => i.slug?.startsWith("ozparts-"));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-sm pt-8 overflow-y-auto pb-8" data-testid="order-detail-modal">
      <div className="bg-[#0D0D0D] border border-[#1E1E1E] rounded-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#1E1E1E]">
          <div>
            <h2 className="text-white font-bold text-lg">Bestelling #{order.id?.slice(0, 8).toUpperCase()}</h2>
            <p className="text-[#555] text-xs mt-1">{formatDate(order.created_at)}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={order.status} />
            <button onClick={onClose} className="text-[#666] hover:text-white transition-colors" data-testid="close-order-detail">
              <XCircle size={22} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer */}
          <div className="bg-[#111] rounded-xl p-4 border border-[#1E1E1E]">
            <h3 className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider mb-3">Klantgegevens</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-[#666]">Naam:</span> <span className="text-white ml-1">{order.name}</span></div>
              <div><span className="text-[#666]">Email:</span> <a href={`mailto:${order.email}`} className="text-[#FF3B30] ml-1">{order.email}</a></div>
              <div><span className="text-[#666]">Telefoon:</span> <span className="text-white ml-1">{order.phone || "-"}</span></div>
              <div><span className="text-[#666]">Adres:</span> <span className="text-white ml-1">{order.address}</span></div>
              <div><span className="text-[#666]">Postcode:</span> <span className="text-white ml-1">{order.postcode} {order.city}</span></div>
              <div><span className="text-[#666]">Land:</span> <span className="text-white ml-1">{order.shipping_country || order.country || "NL"}</span></div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-[#111] rounded-xl p-4 border border-[#1E1E1E]">
            <h3 className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider mb-3">Producten</h3>
            {showoffItems.length > 0 && (
              <div className="mb-3">
                <p className="text-[#27AE60] text-[11px] font-bold mb-1.5">SHOWOFF IMPORTS</p>
                {showoffItems.map((item) => (
                  <div key={item.slug || item.manufacturer_item} className="flex justify-between text-sm py-1.5 border-b border-[#1E1E1E] last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs truncate">{item.title}</p>
                      <p className="text-[#555] text-[10px]">{item.manufacturer_item} · {item.brand}</p>
                    </div>
                    <span className="text-[#FF3B30] text-sm font-bold ml-3 whitespace-nowrap">{item.quantity}x {formatPrice(item.price)}</span>
                  </div>
                ))}
              </div>
            )}
            {ozpartsItems.length > 0 && (
              <div>
                <p className="text-[#3498DB] text-[11px] font-bold mb-1.5">OZPARTS</p>
                {ozpartsItems.map((item) => (
                  <div key={item.slug || item.manufacturer_item} className="flex justify-between text-sm py-1.5 border-b border-[#1E1E1E] last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs truncate">{item.title}</p>
                      <p className="text-[#555] text-[10px]">{item.manufacturer_item} · {item.brand}</p>
                    </div>
                    <span className="text-[#FF3B30] text-sm font-bold ml-3 whitespace-nowrap">{item.quantity}x {formatPrice(item.price)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-[#1E1E1E] space-y-1.5">
              {order.subtotal != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#888]">Subtotaal</span>
                  <span className="text-white">{formatPrice(order.subtotal)}</span>
                </div>
              )}
              {order.shipping_cost != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#888]">Verzendkosten</span>
                  <span className={order.shipping_cost === 0 ? "text-[#27AE60] font-semibold" : "text-white"}>
                    {order.shipping_cost === 0 ? "Gratis" : formatPrice(order.shipping_cost)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-1">
                <span className="text-white">Totaal</span>
                <span className="text-[#FF3B30]">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          {order.notes && (
            <div className="bg-[#111] rounded-xl p-4 border border-[#1E1E1E]">
              <h3 className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider mb-2">Opmerkingen</h3>
              <p className="text-white text-sm">{order.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="bg-[#111] rounded-xl p-4 border border-[#1E1E1E] space-y-4">
            <h3 className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">Acties</h3>

            {/* Status update */}
            <div className="flex items-center gap-3">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="flex-1 bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF3B30] focus:outline-none"
                data-testid="order-status-select"
              >
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
              <Button onClick={handleStatusUpdate} disabled={saving || newStatus === order.status} className="bg-[#3498DB] hover:bg-[#2980B9] text-white text-sm px-4" data-testid="update-status-btn">
                Status wijzigen
              </Button>
            </div>

            {/* Ship order */}
            {(order.status === "paid" || order.status === "processing") && (
              <div className="border-t border-[#1E1E1E] pt-4 space-y-3">
                <p className="text-white text-sm font-semibold flex items-center gap-2"><Truck size={16} className="text-[#8E44AD]" /> Bestelling verzenden</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#888] text-xs block mb-1">Track & Trace</label>
                    <Input data-testid="tracking-code-input" value={trackingCode} onChange={(e) => setTrackingCode(e.target.value)} className="bg-[#0A0A0A] border-[#1E1E1E] text-white text-sm" placeholder="3SXXXX000000" />
                  </div>
                  <div>
                    <label className="text-[#888] text-xs block mb-1">Vervoerder</label>
                    <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-2 text-white text-sm" data-testid="carrier-select">
                      <option value="PostNL">PostNL</option>
                      <option value="DPD">DPD</option>
                      <option value="DHL">DHL</option>
                      <option value="UPS">UPS</option>
                    </select>
                  </div>
                </div>
                <Button onClick={handleShip} disabled={saving} className="w-full bg-[#8E44AD] hover:bg-[#7D3C98] text-white font-semibold" data-testid="ship-order-btn">
                  <Send size={16} className="mr-2" /> Verzenden & Klant informeren
                </Button>
              </div>
            )}

            {order.tracking_code && (
              <div className="bg-[#8E44AD]/10 border border-[#8E44AD]/30 rounded-lg px-4 py-3">
                <p className="text-[#8E44AD] text-xs font-bold mb-1">Verzonden</p>
                <p className="text-white text-sm font-mono">{order.tracking_code}</p>
                <p className="text-[#888] text-xs mt-1">{order.carrier} · {formatDate(order.shipped_at)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───
export default function AdminDashboard() {
  const [token, setToken] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState("orders"); // orders | invoices | vat

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Verify token on mount
  useEffect(() => {
    if (!token) { setAuthChecked(true); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthChecked(true))
      .catch(() => { setToken(null); setAuthChecked(true); });
  }, [token]);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, { params: { status: statusFilter, page, limit: 20 }, headers });
      setOrders(data.orders);
      setTotalPages(data.pages);
    } catch (err) {
      if (err.response?.status === 401) { setToken(null); }
    }
    setLoading(false);
  }, [token, statusFilter, page, headers]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/orders/stats`, { headers });
      setStats(data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Statistieken laden mislukt");
    }
  }, [token, headers]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleLogin = (t) => { setToken(t); setAuthChecked(true); };
  const handleLogout = () => { setToken(null); };

  const refreshOrders = () => { fetchOrders(); fetchStats(); setSelectedOrder(null); };

  if (!authChecked) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 size={32} className="text-[#FF3B30] animate-spin" /></div>;
  if (!token) return <><Toaster position="top-right" /><AdminLogin onLogin={handleLogin} /></>;

  const STATUSES = [
    { key: "all", label: "Alle" },
    { key: "paid", label: "Betaald" },
    { key: "processing", label: "Verwerking" },
    { key: "shipped", label: "Verzonden" },
    { key: "delivered", label: "Afgeleverd" },
    { key: "pending", label: "Wachtend" },
    { key: "expired", label: "Verlopen" },
    { key: "cancelled", label: "Geannuleerd" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="bg-[#111] border-b border-[#1E1E1E] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-[#FF3B30] text-xl font-bold font-['Orbitron'] tracking-wider">TTW</h1>
            <span className="text-[#333] text-xl">|</span>
            <span className="text-white text-sm font-semibold">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-[#888] hover:text-white text-sm transition-colors" data-testid="admin-back-to-site">Naar website</a>
            <Button onClick={handleLogout} variant="ghost" className="text-[#888] hover:text-[#FF3B30] text-sm" data-testid="admin-logout-btn">
              <LogOut size={16} className="mr-2" /> Uitloggen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 border-b border-[#1E1E1E]" data-testid="admin-tabs">
          {[
            { key: "orders", label: "Bestellingen", icon: ShoppingBag },
            { key: "invoices", label: "Facturen", icon: FileText },
            { key: "manual", label: "Handmatige Betalingen", icon: Smartphone },
            { key: "pricing", label: "Marge", icon: TrendingUp },
            { key: "vat", label: "BTW Rapport", icon: Percent },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all ${activeTab === t.key ? "text-[#FF3B30] border-[#FF3B30]" : "text-[#666] border-transparent hover:text-white"}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === "invoices" && <InvoicesTab token={token} />}
        {activeTab === "manual" && <ManualPaymentsTab token={token} />}
        {activeTab === "pricing" && <PricingTab token={token} />}
        {activeTab === "vat" && <VatReportTab token={token} />}
        {activeTab === "orders" && (
        <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
            {[
              { label: "Totaal", value: stats.total, icon: ShoppingBag, color: "#888" },
              { label: "Verwerking", value: stats.processing, icon: Package, color: "#3498DB" },
              { label: "Verzonden", value: stats.shipped, icon: Truck, color: "#8E44AD" },
              { label: "Afgeleverd", value: stats.delivered, icon: CheckCircle, color: "#6AB04C" },
              { label: "Wachtend", value: stats.pending, icon: Clock, color: "#F39C12" },
              { label: "Omzet", value: formatPrice(stats.revenue), icon: BarChart3, color: "#FF3B30" },
            ].map((s) => (
              <div key={s.label} className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-2">
                  <s.icon size={14} style={{ color: s.color }} />
                  <span className="text-[#666] text-xs">{s.label}</span>
                </div>
                <p className="text-white text-xl font-bold" style={{ color: s.label === "Omzet" ? "#FF3B30" : undefined }}>
                  {typeof s.value === "number" ? s.value : s.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide" data-testid="admin-status-filters">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              data-testid={`filter-${s.key}`}
              onClick={() => { setStatusFilter(s.key); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all border ${statusFilter === s.key ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#111] text-[#666] border-[#1E1E1E] hover:text-white"}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Order table */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden" data-testid="admin-orders-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E1E1E]">
                  <th className="text-left px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Bestelling</th>
                  <th className="text-left px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Klant</th>
                  <th className="text-left px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Datum</th>
                  <th className="text-left px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Totaal</th>
                  <th className="text-right px-5 py-3.5 text-[#666] text-xs font-semibold uppercase tracking-wider">Actie</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-16"><Loader2 size={24} className="text-[#FF3B30] animate-spin mx-auto" /></td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-16 text-[#555]">Geen bestellingen gevonden</td></tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id} className="border-b border-[#1E1E1E]/50 hover:bg-[#1E1E1E]/20 transition-colors cursor-pointer" onClick={() => setSelectedOrder(order)} data-testid={`order-row-${order.id?.slice(0, 8)}`}>
                      <td className="px-5 py-3.5">
                        <span className="text-[#FF3B30] font-mono font-bold text-sm">#{order.id?.slice(0, 8).toUpperCase()}</span>
                        <p className="text-[#555] text-[10px] mt-0.5">{(order.items || []).length} product(en)</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-white text-sm">{order.name}</p>
                        <p className="text-[#555] text-xs">{order.email}</p>
                      </td>
                      <td className="px-5 py-3.5 text-[#888] text-sm">{formatDate(order.created_at)}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={order.status} /></td>
                      <td className="px-5 py-3.5 text-right text-[#FF3B30] font-bold text-sm">{formatPrice(order.total)}</td>
                      <td className="px-5 py-3.5 text-right">
                        <Button variant="ghost" size="sm" className="text-[#888] hover:text-white" data-testid={`view-order-${order.id?.slice(0, 8)}`}>
                          <Eye size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#1E1E1E]">
              <span className="text-[#555] text-sm">Pagina {page} van {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-[#888]" data-testid="admin-prev-page"><ChevronLeft size={16} /></Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="text-[#888]" data-testid="admin-next-page"><ChevronRight size={16} /></Button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetail order={selectedOrder} token={token} onClose={() => setSelectedOrder(null)} onUpdate={refreshOrders} />
      )}
    </div>
  );
}
